import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Validators, UntypedFormBuilder, UntypedFormGroup, UntypedFormArray, UntypedFormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Timestamp } from 'firebase/firestore';
import { map } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { uniqueEntryValidator } from 'src/app/shared/directives/unique-entry.directive';
import { ERROR, SUCCESS } from 'src/app/global';
import { PatientService } from 'src/app/calvin-emr-shared/services/patient.service';
import { UserService } from 'src/app/calvin-emr-shared/services/user.service';
import { Patient } from 'src/app/calvin-emr-shared/models/patient.model';
import { SimPerson } from 'src/app/calvin-emr-shared/models/sim-person.model';
import { MedicalCondition } from 'src/app/calvin-emr-shared/models/medical-condition.model';
import { firstNames, lastNames } from '../names';
import { codeStatusOptions, maxAge, minMeasurement, sexOptions, patientTypeOptions } from './patient-form.metadata';
import { PATH_PATIENTS, PATH_PATIENT_DASHBOARD } from '../../patients.global';
import { Store } from '@ngrx/store';
import { selectUser } from 'src/app/calvin-emr-shared/state/auth/auth.selector';
import { User } from 'src/app/calvin-emr-shared/models/user.model';
import { ToastService } from 'src/app/calvin-emr-shared/services/toast.service';
import { NgIf, NgFor } from '@angular/common';
import { ButtonDirective } from 'primeng/button';
import { FieldsetModule } from 'primeng/fieldset';
import { InputTextModule } from 'primeng/inputtext';
import { DividerModule } from 'primeng/divider';
import { FileUploadModule } from 'primeng/fileupload';
import { EmrInputComponent } from "../../../shared/emr-input/emr-input.component";
import { COLLECTION_PATIENTS, ROLE_ADMIN } from 'src/app/calvin-emr-shared/global';

@Component({
  selector: 'app-patient-form',
  templateUrl: './patient-form.component.html',
  styleUrls: ['./patient-form.component.scss'],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    NgIf,
    ButtonDirective,
    FieldsetModule,
    NgFor,
    InputTextModule,
    DividerModule,
    FileUploadModule,
    EmrInputComponent
  ],
})
export class PatientFormComponent implements OnInit, OnDestroy {
  @Input() type = '';
  @Input() prefill = false;
  @Input() patientId: string | null = null;

  // Limit constants for certain inputs
  minMeasurement = minMeasurement;
  maxAge = maxAge;
  maxWeight = 300;
  maxFeet = 10;
  maxInches = 11;
  codeStatusOptions = codeStatusOptions;
  sexOptions = sexOptions;
  patientTypeOptions = patientTypeOptions;

  patient: Patient = new Patient();

  /**
   * The form containing all controls.
   */
  patientForm = this.fb.group({
    firstName: ['', Validators.required],
    lastName: ['', Validators.required],
    middleName: [''],
    id: ['', Validators.required], // also used for MRN

    // General info
    generalInfo: this.fb.group({
      codeStatus: ['', Validators.required],
      dob: ['', [Validators.required]],
      age: [
        null,
        [Validators.required, Validators.min(this.minMeasurement), Validators.max(this.maxAge)],
      ],
      sex: ['', Validators.required],
      weight: [
        null,
        [Validators.required, Validators.min(this.minMeasurement), Validators.max(this.maxWeight)],
      ],
      heightFeet: [
        null,
        [Validators.required, Validators.min(this.minMeasurement), Validators.max(this.maxFeet)],
      ],
      heightInches: [
        null,
        [Validators.required, Validators.min(this.minMeasurement), Validators.max(this.maxInches)],
      ],
      isolation: ['', Validators.required],
      patientType: ['unassigned', Validators.required],
      roomNumber: [''],
      location: [''],
    }),

    // Interdisciplinary team
    interdisciplinaryTeam: this.fb.array([]),
    activeMedicalProblems: this.fb.array([]),
    allergies: this.fb.array([]),

    // profile pic name
    profilePicName: [''],
  });

  /**
   * The patient's profile picture.
   */
  profilePic?: File;

  /**
   * All MRNs that are already in use in the "patients" collection in Firestore.
   */
  mrns: string[] = [];

  /**
   * Stores the subscription to the Observable that retrieves all patients in
   * the "patients" collection.
   */
  mrnsSub?: Subscription;
  currentUserSub: Subscription;
  currentUser = new User();

  didSubmit = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private fb: UntypedFormBuilder,
    private patientService: PatientService,
    private userService: UserService,
    private store: Store,
    private toastService: ToastService
  ) {
    this.currentUserSub = this.store.select(selectUser).subscribe((user: User | null) => {
      if (user) {
        this.currentUser = user;
      }
    });
  }

  /**
   * Get all MRNs that are already in use by existing patients.
   */
  ngOnInit(): void {
    // Push each existing patient MRN into the mrns list
    this.mrnsSub = this.patientService
      .getAllMRNs()
      .pipe(
        map((actionArray) => {
          actionArray.forEach((val) => {
            this.mrns.push(val.payload.doc.id);
          });
        })
      )
      .subscribe(() => {
        // Add validator to MRN FormControl to ensure it is unique
        this.patientForm.controls['id'].addValidators([uniqueEntryValidator(this.mrns)]);
      });

    this.prefillForm();
  }

  ngOnDestroy(): void {
    this.mrnsSub?.unsubscribe();
    this.currentUserSub.unsubscribe();
  }

  randomizeForm() {
    const firstNameIndex = this.getRandomIndexFromArray(firstNames);
    const lastNameIndex = this.getRandomIndexFromArray(lastNames);
    const codeStatusIndex = this.getRandomIndexFromArray(codeStatusOptions);
    const patientTypeIndex = this.getRandomIndexFromArray(patientTypeOptions);

    this.generateMRN();
    this.patientForm.patchValue({
      firstName: firstNames[firstNameIndex],
      lastName: lastNames[lastNameIndex],

      // General info
      generalInfo: {
        codeStatus: codeStatusOptions[codeStatusIndex].label,
        sex: Math.round(Math.random()) ? 'Male' : 'Female',
        weight: Math.random() * 300,
        heightFeet: Math.random() * 10,
        heightInches: Math.random() * 11,
        isolation: 'None',
        patientType: patientTypeOptions[patientTypeIndex].label,
        roomNumber: Math.floor(Math.random() * 100).toString(),
        location: '',
      },
    });
  }

  prefillForm() {
    if (this.prefill) {
      const pid = this.route.snapshot.paramMap.get('id');
      if (pid) {
        const patientSubscription = this.patientService
          .getPatient(pid, COLLECTION_PATIENTS)
          .subscribe((res) => {
            const patient = new Patient();
            const obj = { id: res.payload.id, ...(res.payload.data() as object) };
            Object.assign(patient, obj);
            patient.toJSObject();
            this.patient = patient;
            this.setFormValues();
            patientSubscription.unsubscribe();
          });
      }
    }
  }

  setFormValues() {
    // Split height into feet and inches
    const totalInches = this.patient.getHeightInches();
    const feet = Math.floor(totalInches / 12);
    const inches = totalInches % 12;

    this.patientForm.patchValue({
      firstName: this.patient.firstName,
      middleName: this.patient.middleName,
      lastName: this.patient.lastName,
      id: this.patient.id,

      // General info
      generalInfo: {
        codeStatus: this.patient.getCodeStatus(),
        dob: this.patient.getDOB().toDate(),
        age: this.patient.getAge(),
        sex: this.patient.getSex(),
        weight: this.patient.getWeight(),
        heightFeet: feet,
        heightInches: inches,
        isolation: this.patient.getIsolation(),
        patientType: this.patient.getType(),
        roomNumber: this.patient.getRoomNumber(),
        location: this.patient.getLocation(),
      },

      // profile pic name
      profilePicName: this.patient.profilePicName,
    });

    // Disable ID field
    this.idFormControl.disable();

    // Set medical info
    this.patient.interdisciplinaryTeam.forEach((member: SimPerson) => {
      this.addInterdisciplinaryTeamFormGroup(member);
    });
    this.patient.activeMedicalProblems.forEach((problem: MedicalCondition) => {
      this.addMedicalProblemFormGroup(problem);
    });
    this.patient.allergies.forEach((allergy: MedicalCondition) => {
      this.addAllergyFormGroup(allergy);
    });
  }

  /**
   * Generate a random 6-digit number, where the first digit
   * is never 0. Set the ID FormControl to this value.
   */
  generateMRN() {
    const mrn = '' + Math.floor(1000000000 + Math.random() * 9000000000);
    this.idFormControl.setValue(mrn);
  }

  /**
   * Adds a FormGroup to a FormArray, depending on the desired FormArray.
   * Default values of the controls in the FormGroup are empty strings.
   *
   * @param type Specifies which FormGroup to add.
   */
  addEmptyFormGroup(type: string) {
    switch (type.toLocaleLowerCase()) {
      case 'team': {
        this.addInterdisciplinaryTeamFormGroup();
        break;
      }
      case 'problem': {
        this.addMedicalProblemFormGroup();
        break;
      }
      case 'allergy': {
        this.addAllergyFormGroup();
        break;
      }
    }
  }

  /**
   * Adds a FormGroup for an interdisciplinary team member to the form.
   *
   * @param member Optional parameter that is used to prefill the FormGroup's control values.
   */
  addInterdisciplinaryTeamFormGroup(member?: SimPerson) {
    const specialization = member?.specialization ? member.specialization : '';
    const name = member?.name ? member.name : '';
    const title = member?.title ? member.title : '';

    const formGroup = this.fb.group({
      specialization: [specialization, Validators.required],
      name: [name, Validators.required],
      title: [title],
    });

    this.interdisciplinaryTeamFormArray.push(formGroup);
  }

  /**
   * Adds a FormGroup for an active medical problem to the form.
   *
   * @param problem Optional parameter that is used to prefill the FormGroup's control values.
   */
  addMedicalProblemFormGroup(problem?: MedicalCondition) {
    const name = problem?.name ? problem.name : '';
    const altName = problem?.altName ? problem.altName : '';

    const formGroup = this.fb.group({
      name: [name, Validators.required],
      altName: [altName],
    });

    this.activeMedProblemsFormArray.push(formGroup);
  }

  /**
   * Adds a FormGroup for an allergy to the form.
   *
   * @param allergy Optional parameter that is used to prefill the FormGroup's control values.
   */
  addAllergyFormGroup(allergy?: MedicalCondition) {
    const name = allergy?.name ? allergy.name : '';
    const altName = allergy?.altName ? allergy.altName : '';

    const formGroup = this.fb.group({
      name: [name, Validators.required],
      altName: [altName],
    });

    this.allergiesFormArray.push(formGroup);
  }

  /**
   * Deletes the FormGroup at the specified index in the specified FormArray.
   *
   * @param index The index of the FormGroup to delete in the FormArray.
   */
  deleteFormGroup(index: number, type: string) {
    switch (type.toLocaleLowerCase()) {
      case 'team': {
        this.interdisciplinaryTeamFormArray.removeAt(index);
        break;
      }
      case 'problem': {
        this.activeMedProblemsFormArray.removeAt(index);
        break;
      }
      case 'allergy': {
        this.allergiesFormArray.removeAt(index);
        break;
      }
      default: {
        console.log(type, 'is not a valid FormArray descriptor!');
      }
    }
  }

  /**
   * Get the image selected as a File object, and store the object and its name.
   *
   * @param event Object passed from p-fileUpload containing file information.
   */
  onFileChange(event: any) {
    const files = event.currentFiles as File[];
    if (files && files.length > 0) {
      this.profilePic = event.currentFiles[0];
      this.profilePicNameFormControl.setValue(this.profilePic?.name);
    } else {
      this.profilePic = undefined;
      this.profilePicNameFormControl.setValue('');
    }
  }

  /**
   *
   * @returns A Patient object using the data from the form.
   */
  getPatientFromForm() {
    // Create new patient or edit existing one depending on if form is create or edit type
    const patient = this.isEditType ? this.patient : new Patient();

    // Change height in feet to height in inches, store only inches in patient
    const heightInches = this.heightFeetFormControl.value * 12 + this.heightInchesFormControl.value;

    // Remove heightFeet from the object, since we converted it to inches
    const formValue = this.patientForm.getRawValue();
    delete formValue.heightFeet;

    // Assign form value to Patient object
    Object.assign(patient, formValue);

    // Set specific fields
    patient.setHeight(heightInches);
    patient.setDOB(this.getDOBTimestamp());
    patient.setMRN(patient.id);

    return patient;
  }

  /**
   * Create patient using the patient form and stores height information as inch unit.
   */
  upsertPatient(): void {
    this.didSubmit = true;
    this.patient = this.getPatientFromForm();

    // If patient is being created
    if (!this.patientId) {
      // Add patient to user's patient list
      this.userService.addPatientIdsToUser([this.patient.getId()], this.currentUser.id);
    }

    // Send formatted patient to PatientService
    this.patientService.upsertPatient(this.patient, COLLECTION_PATIENTS, this.profilePic).subscribe(
      () => {
        this.toastService.clearAndAddOne({
          severity: SUCCESS,
          summary: 'Success',
          detail: `Created patient ${this.patient.getLastFirstMiddleName()}!`,
        });

        this.router.navigate([PATH_PATIENTS, PATH_PATIENT_DASHBOARD, ROLE_ADMIN, this.patient.id]);
        this.patientForm.reset();
      },
      (error) => {
        this.toastService.clearAndAddOne({
          severity: ERROR,
          summary: 'Error',
          detail: `Unable to create patient ${this.patient.getLastFirstMiddleName()}: ${error}`,
        });
      },
      () => {
        this.didSubmit = false;
      }
    );
  }

  /**
   *
   * @returns The date of birth as a Timestamp object.
   */
  getDOBTimestamp() {
    const date = new Date(this.dobFormControl.value);
    return Timestamp.fromDate(date);
  }

  getRandomIndexFromArray(array: any[]): number {
    return Math.floor(Math.random() * array.length);
  }

  get isCreateType() {
    return this.type === 'create-patient';
  }

  get isEditType() {
    return this.type === 'edit-patient';
  }

  get pageTitle() {
    if (this.isCreateType) {
      return 'Create Patient';
    } else {
      return 'Edit Patient';
    }
  }

  get submitButtonLabel() {
    if (this.isCreateType) {
      return 'Create Patient';
    } else {
      return 'Update Patient';
    }
  }

  // Form Controls

  get firstNameFormControl(): UntypedFormControl {
    return this.patientForm.controls.firstName as UntypedFormControl;
  }

  get middleNameFormControl(): UntypedFormControl {
    return this.patientForm.controls.middleName as UntypedFormControl;
  }

  get lastNameFormControl(): UntypedFormControl {
    return this.patientForm.controls.lastName as UntypedFormControl;
  }

  get idFormControl(): UntypedFormControl {
    return this.patientForm.controls.id as UntypedFormControl;
  }

  get generalInfoFormGroup(): UntypedFormGroup {
    return this.patientForm.get('generalInfo') as UntypedFormGroup;
  }

  get codeStatusFormControl(): UntypedFormControl {
    return this.patientForm.get('generalInfo.codeStatus') as UntypedFormControl;
  }

  get dobFormControl(): UntypedFormControl {
    return this.patientForm.get('generalInfo.dob') as UntypedFormControl;
  }

  get ageFormControl(): UntypedFormControl {
    return this.patientForm.get('generalInfo.age') as UntypedFormControl;
  }

  get sexFormControl(): UntypedFormControl {
    return this.patientForm.get('generalInfo.sex') as UntypedFormControl;
  }

  get weightFormControl(): UntypedFormControl {
    return this.patientForm.get('generalInfo.weight') as UntypedFormControl;
  }

  get heightFeetFormControl(): UntypedFormControl {
    return this.patientForm.get('generalInfo.heightFeet') as UntypedFormControl;
  }

  get heightInchesFormControl(): UntypedFormControl {
    return this.patientForm.get('generalInfo.heightInches') as UntypedFormControl;
  }

  get isolationFormControl(): UntypedFormControl {
    return this.patientForm.get('generalInfo.isolation') as UntypedFormControl;
  }

  get patientTypeFormControl(): UntypedFormControl {
    return this.patientForm.get('generalInfo.patientType') as UntypedFormControl;
  }

  get roomNumberFormControl(): UntypedFormControl {
    return this.patientForm.get('generalInfo.roomNumber') as UntypedFormControl;
  }

  get locationFormControl(): UntypedFormControl {
    return this.patientForm.get('generalInfo.location') as UntypedFormControl;
  }

  // Form Arrays

  get interdisciplinaryTeamFormArray(): UntypedFormArray {
    return this.patientForm.controls.interdisciplinaryTeam as UntypedFormArray;
  }

  get activeMedProblemsFormArray(): UntypedFormArray {
    return this.patientForm.controls.activeMedicalProblems as UntypedFormArray;
  }

  get allergiesFormArray(): UntypedFormArray {
    return this.patientForm.controls.allergies as UntypedFormArray;
  }

  get profilePicNameFormControl(): UntypedFormControl {
    return this.patientForm.controls.profilePicName as UntypedFormControl;
  }

}
